import { Link, useLocation } from "react-router";

import { cn } from "~/lib/utils";

/** Every path that counts as Home, so the shop pages do not leave both entries unmarked. */
const HOME_PATHS = ["/", "/shop"];
export const CINEMA_PATH = "/cinema";

function isCinema(pathname: string): boolean {
  return pathname === CINEMA_PATH || pathname.startsWith(`${CINEMA_PATH}/`);
}

interface ModeSwitchProps {
  /** Mobile draws an underline under the active entry; desktop sits in the nav pill and marks
   *  the active entry by weight, matching the other items in that pill. */
  variant: "mobile" | "desktop";
  /** Sitting directly on black. The desktop variant never is: it lives in a light pill. */
  dark?: boolean;
}

/** Home / Cinema. The two halves of the site, and the only way back from one to the other. */
export function ModeSwitch({ variant, dark = false }: ModeSwitchProps) {
  const { pathname } = useLocation();
  const cinemaActive = isCinema(pathname);
  const homeActive = HOME_PATHS.includes(pathname) || (!cinemaActive && pathname.startsWith("/shop"));

  const entry = (id: string, to: string, label: string, active: boolean, badge?: string) => (
    <Link
      id={id}
      to={to}
      className={cn(
        "flex cursor-pointer items-center gap-[4px] whitespace-nowrap",
        variant === "mobile"
          ? "border-b-2 pb-[6px] text-[14px] leading-[20px]"
          : "rounded-[6px] px-[8px] py-[8px] text-[14px] leading-[20px]",
        active ? "font-semibold" : "font-medium",
        dark ? (active ? "text-white" : "text-white/70") : active ? "text-neutral-900" : "text-neutral-500",
        variant === "mobile" && (active ? (dark ? "border-white" : "border-neutral-900") : "border-transparent")
      )}
    >
      {label}
      {badge && (
        <span className="bg-brand-500 text-text-primary rounded-[4px] px-[4px] py-[1px] text-[10px] leading-[14px] font-semibold">
          {badge}
        </span>
      )}
    </Link>
  );

  return (
    <div className={cn("flex items-center", variant === "mobile" ? "gap-[20px]" : "gap-[4px]")}>
      {/* The variant is part of the id: both render on every page and CSS picks one, so a
          shared id would report two elements under one name and hide which form factor converted. */}
      {entry(`header-mode-home-${variant}`, "/shop", "Home", homeActive)}
      {entry(`header-mode-cinema-${variant}`, CINEMA_PATH, "Cinema", cinemaActive, "New")}
    </div>
  );
}
